import { memo, type KeyboardEvent as ReactKeyboardEvent, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { showToast } from '@/components/Layout/Toast';
import { ConfirmationDialog } from '@/components/UI';
import { useAgentStream, type AgentExecutionMode, type AgentSseEvent } from '@/hooks/useAgentStream';
import { usePageContext } from '@context/PageContext';
import { InlineEditableTitle } from './InlineEditableTitle';
import { AssistantMarkdownMessage } from './AssistantMarkdownMessage';
import { AutopilotActionBar } from './AutopilotActionBar';
import { extractAgentMode, extractAutopilotPlanId } from './agentSessionContext';

type ChatMessage =
    | { id?: number; role: 'user' | 'assistant'; content: string }
    | {
          id?: number;
          role: 'tool_call';
          toolCallId: string;
          toolName: string;
          toolInputJson: string;
          requiresConfirmation: boolean;
          confirmationStatus: 'pending' | 'approved' | 'rejected' | null;
          actionDescription?: string;
      }
    | { id?: number; role: 'tool_result'; toolCallId: string; toolName: string; toolResultJson: string };

type ChatWindowProps = {
    sessionId: number | null;
    setSessionId: (sessionId: number | null) => void;
    messagesLimit?: number;
    autoFocus?: boolean;
    pollSession?: boolean;
    isSidebarVisible?: boolean;
    onToggleSidebar?: () => void;
};

const DISPLAYABLE_ROLES = new Set<string>(['user', 'assistant', 'tool_call', 'tool_result']);
const CHAT_EXECUTION_MODE_STORAGE_KEY = 'agentChatExecutionMode';
const CHAT_AUTO_APPROVE_STORAGE_KEY = 'agentChatAutoApproveEnabled';
const CHAT_SHOW_TOOLS_STORAGE_KEY = 'agentChatShowTools';

function isDisplayableRole(role: string): role is ChatMessage['role'] {
    return DISPLAYABLE_ROLES.has(role);
}

function formatInferenceDefaultLabel(displayName: string | null): string {
    const raw = (displayName ?? '').trim();
    if (!raw) return 'Default';
    const withoutPrefix = raw.startsWith('Reasoning:') ? raw.replace(/^Reasoning:\s*/i, '') : raw;
    return `Default (${withoutPrefix || raw})`;
}

function prettyJson(raw: string): string {
    try {
        const parsed = JSON.parse(raw) as unknown;
        return JSON.stringify(parsed, null, 2);
    } catch {
        return raw;
    }
}

function formatModelLabel(params: { displayName: string; provider: string; isDefault: boolean }): string {
    const providerName = params.provider === 'anthropic' ? 'Anthropic' : params.provider === 'openai' ? 'OpenAI' : params.provider;
    const defaultSuffix = params.isDefault ? ' (default)' : '';
    return `${providerName} — ${params.displayName}${defaultSuffix}`;
}

const ChatMessagesPanel = memo(
    ({
        messages,
        highlightedId,
        effectiveShowTools,
        isStreaming,
        isBusy,
        status,
        streamingAssistantText,
        decideConfirmation,
        endRef
    }: {
        messages: ChatMessage[];
        highlightedId: number | null;
        effectiveShowTools: boolean;
        isStreaming: boolean;
        isBusy: boolean;
        status: string | null;
        streamingAssistantText: string;
        decideConfirmation: (params: { toolCallId: string; approved: boolean }) => Promise<void>;
        endRef: RefObject<HTMLDivElement | null>;
    }) => {
        return (
            <div className="flex-1 overflow-auto px-3 py-2 space-y-2">
                {messages.length === 0 && !isBusy && (
                    <div className="text-sm text-zinc-400">
                        Ask me about your current page, experiments, strategies, screeners, and results.
                    </div>
                )}

                {messages.map((m, idx) => {
                    const messageId = m.id ?? undefined;
                    const isHighlighted = messageId !== undefined && messageId === highlightedId;
                    const highlightCls = isHighlighted ? ' ring-2 ring-blue-500/50' : '';

                    if (m.role === 'user' || m.role === 'assistant') {
                        const isUser = m.role === 'user';

                        const bubbleCls = isUser
                            ? 'bg-blue-600/40 border-blue-500/50 text-blue-50'
                            : 'bg-zinc-800/70 border-zinc-700 text-zinc-100';

                        const alignCls = isUser ? 'justify-end' : 'justify-start';

                        return (
                            <div
                                className={`flex ${alignCls}`}
                                id={messageId ? `chat-message-${messageId}` : undefined}
                                key={m.id ?? `local-${idx}`}
                            >
                                <div
                                    className={`max-w-[90%] rounded-lg border px-3 py-2 text-sm ${
                                        isUser ? 'whitespace-pre-wrap' : ''
                                    } ${bubbleCls}${highlightCls}`}
                                >
                                    {isUser ? m.content : <AssistantMarkdownMessage enableDetailsCollapse markdown={m.content} />}
                                </div>
                            </div>
                        );
                    }

                    if (m.role === 'tool_call') {
                        if (!effectiveShowTools && !m.requiresConfirmation) return null;
                        return (
                            <div
                                className="flex justify-start"
                                id={messageId ? `chat-message-${messageId}` : undefined}
                                key={m.id ?? `local-${idx}`}
                            >
                                <div
                                    className={`max-w-[90%] rounded-lg border border-zinc-700 bg-zinc-950/50 px-3 py-2 text-xs text-zinc-200${highlightCls}`}
                                >
                                    <div className="font-semibold text-zinc-100">Tool call: {m.toolName}</div>
                                    {m.requiresConfirmation && (
                                        <div className="mt-1 text-[11px] text-zinc-300">
                                            {m.actionDescription ?? 'This action requires confirmation.'}
                                        </div>
                                    )}
                                    <pre className="mt-1 text-[11px] text-zinc-300 whitespace-pre-wrap">{prettyJson(m.toolInputJson)}</pre>
                                    {m.requiresConfirmation && (
                                        <div className="mt-2 flex items-center gap-2">
                                            <button
                                                className="text-xs px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white"
                                                disabled={isStreaming || m.confirmationStatus !== 'pending'}
                                                type="button"
                                                onClick={() => {
                                                    void decideConfirmation({ toolCallId: m.toolCallId, approved: true });
                                                }}
                                            >
                                                Approve
                                            </button>
                                            <button
                                                className="text-xs px-2 py-1 rounded-md bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-800 text-white"
                                                disabled={isStreaming || m.confirmationStatus !== 'pending'}
                                                type="button"
                                                onClick={() => {
                                                    void decideConfirmation({ toolCallId: m.toolCallId, approved: false });
                                                }}
                                            >
                                                Reject
                                            </button>
                                            <div className="text-[11px] text-zinc-400">Status: {m.confirmationStatus ?? '—'}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    }

                    if (m.role === 'tool_result') {
                        if (!effectiveShowTools) return null;
                        return (
                            <div
                                className="flex justify-start"
                                id={messageId ? `chat-message-${messageId}` : undefined}
                                key={m.id ?? `local-${idx}`}
                            >
                                <div
                                    className={`max-w-[90%] rounded-lg border border-zinc-700 bg-zinc-950/50 px-3 py-2 text-xs text-zinc-200${highlightCls}`}
                                >
                                    <div className="font-semibold text-zinc-100">Tool result: {m.toolName}</div>
                                    <pre className="mt-1 text-[11px] text-zinc-300 whitespace-pre-wrap">{prettyJson(m.toolResultJson)}</pre>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div
                            className="flex justify-start"
                            id={messageId ? `chat-message-${messageId}` : undefined}
                            key={m.id ?? `local-${idx}`}
                        >
                            <div
                                className={`max-w-[90%] rounded-lg border border-zinc-700 bg-zinc-950/50 px-3 py-2 text-xs text-zinc-200${highlightCls}`}
                            >
                                <div className="text-zinc-300">Unsupported message type.</div>
                            </div>
                        </div>
                    );
                })}

                {isBusy && (
                    <div className="flex justify-start">
                        <div className="max-w-[90%] rounded-lg border px-3 py-2 text-sm whitespace-pre-wrap bg-zinc-800/70 border-zinc-700 text-zinc-100">
                            {streamingAssistantText || (
                                <span className="text-zinc-400">
                                    {status === 'queued'
                                        ? 'Waiting…'
                                        : status === 'waiting_confirmation'
                                          ? 'Waiting for confirmation…'
                                          : status === 'stalled'
                                            ? 'Run stalled.'
                                            : 'Thinking…'}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                <div ref={endRef} />
            </div>
        );
    }
);

const ChatInput = memo(
    (props: {
        autoFocus: boolean;
        isBusy: boolean;
        currentRunId: number | null;
        onSend: (message: string) => Promise<void>;
        onStop: () => void;
    }) => {
        const inputRef = useRef<HTMLTextAreaElement | null>(null);
        const [draft, setDraft] = useState('');

        useEffect(() => {
            if (!props.autoFocus || !inputRef.current) return;
            inputRef.current.focus();
        }, [props.autoFocus]);

        const onKeyDown = useCallback(
            (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (props.isBusy) return;
                    const trimmed = draft.trim();
                    if (!trimmed) return;
                    void props.onSend(trimmed);
                    setDraft('');
                }
            },
            [draft, props.isBusy, props.onSend]
        );

        return (
            <div className="flex gap-2 w-full">
                <textarea
                    className="flex-1 rounded-md bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    disabled={props.isBusy}
                    placeholder="Type a message…"
                    ref={inputRef}
                    rows={2}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                />
                {props.isBusy && (
                    <button
                        className="rounded-md bg-red-700 hover:bg-red-600 disabled:bg-zinc-700 px-3 py-2 text-sm font-medium text-white"
                        title={props.currentRunId ? 'Cancel current run' : 'Stop'}
                        type="button"
                        onClick={props.onStop}
                    >
                        Stop
                    </button>
                )}
                <button
                    className="rounded-md bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 px-3 py-2 text-sm font-medium text-white"
                    disabled={props.isBusy || draft.trim().length === 0}
                    type="button"
                    onClick={() => {
                        if (props.isBusy) return;
                        const trimmed = draft.trim();
                        if (!trimmed) return;
                        void props.onSend(trimmed);
                        setDraft('');
                    }}
                >
                    Send
                </button>
            </div>
        );
    }
);

export function ChatWindow({
    sessionId,
    setSessionId,
    messagesLimit = 500,
    autoFocus = false,
    pollSession = false,
    isSidebarVisible = true,
    onToggleSidebar
}: ChatWindowProps) {
    const queryClient = useQueryClient();
    const { pageContext } = usePageContext();
    const { streamMessage, confirmToolCall, cancelRun, abort, isStreaming, status, error: streamError } = useAgentStream();

    const [executionMode, setExecutionMode] = useState<AgentExecutionMode>(() => {
        if (typeof window === 'undefined') return 'inline';
        const stored = localStorage.getItem(CHAT_EXECUTION_MODE_STORAGE_KEY);
        return stored === 'queued' ? 'queued' : 'inline';
    });

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [streamingAssistantText, setStreamingAssistantText] = useState('');
    const streamingAssistantRef = useRef('');
    const streamingUpdateRafRef = useRef<number | null>(null);
    const [currentRunId, setCurrentRunId] = useState<number | null>(null);

    const [isAutoApproveEnabled, setIsAutoApproveEnabled] = useState(() => {
        if (typeof window === 'undefined') return false;
        return localStorage.getItem(CHAT_AUTO_APPROVE_STORAGE_KEY) === 'true';
    });

    const [showTools, setShowTools] = useState(() => {
        if (typeof window === 'undefined') return false;
        return localStorage.getItem(CHAT_SHOW_TOOLS_STORAGE_KEY) === 'true';
    });

    const [isAutoApproveWarningOpen, setIsAutoApproveWarningOpen] = useState(false);
    const [pendingAutoApproveToolCallIds, setPendingAutoApproveToolCallIds] = useState<string[]>([]);

    const pageContextRef = useRef(pageContext);

    useEffect(() => {
        pageContextRef.current = pageContext;
    }, [pageContext]);

    const endRef = useRef<HTMLDivElement | null>(null);
    const previousSessionIdRef = useRef<number | null>(sessionId);
    const createdSessionIdRef = useRef<number | null>(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        localStorage.setItem(CHAT_EXECUTION_MODE_STORAGE_KEY, executionMode);
    }, [executionMode]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        localStorage.setItem(CHAT_AUTO_APPROVE_STORAGE_KEY, String(isAutoApproveEnabled));
    }, [isAutoApproveEnabled]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        localStorage.setItem(CHAT_SHOW_TOOLS_STORAGE_KEY, String(showTools));
    }, [showTools]);

    useEffect(() => {
        if (previousSessionIdRef.current === sessionId) return;

        const previousSessionId = previousSessionIdRef.current;
        previousSessionIdRef.current = sessionId;

        if (isStreaming) abort();

        setCurrentRunId(null);
        setPendingAutoApproveToolCallIds([]);
        setIsAutoApproveWarningOpen(false);
        setStreamingAssistantText('');
        streamingAssistantRef.current = '';

        if (streamingUpdateRafRef.current !== null && typeof window !== 'undefined') {
            cancelAnimationFrame(streamingUpdateRafRef.current);
            streamingUpdateRafRef.current = null;
        }

        const shouldPreserveMessages = previousSessionId === null && sessionId !== null && createdSessionIdRef.current === sessionId;

        if (!shouldPreserveMessages) {
            setMessages([]);
        } else {
            createdSessionIdRef.current = null;
        }
    }, [abort, isStreaming, sessionId]);

    const flushStreamingAssistant = useCallback(() => {
        const raw = streamingAssistantRef.current;
        if (!raw.trim()) return;

        setMessages((prev) => [...prev, { role: 'assistant', content: raw.trim() }]);
        setStreamingAssistantText('');
        streamingAssistantRef.current = '';
    }, []);

    const sessionQuery = useQuery({
        ...trpc.agent.getSession.queryOptions({ sessionId: sessionId ?? 0, messagesLimit }),
        enabled: sessionId !== null,
        retry: false,
        refetchInterval: (query) => {
            if (sessionId === null) return false;
            if (isStreaming) return false;
            if (pollSession) return 1000;
            return query.state.data?.activeRun ? 1000 : false;
        },
        refetchIntervalInBackground: pollSession
    });

    const inferenceProfilesQuery = useQuery({
        ...trpc.agent.listInferenceProfiles.queryOptions({ limit: 50 }),
        retry: false
    });

    const modelsQuery = useQuery({
        ...trpc.agent.listModels.queryOptions({}),
        retry: false
    });

    const setSessionModelMutation = useTrpcMutation(
        trpc.agent.setSessionModel.mutationOptions({
            onSuccess: async () => {
                if (!sessionId) return;

                await queryClient.invalidateQueries({
                    queryKey: trpc.agent.getSession.queryOptions({ sessionId, messagesLimit }).queryKey
                });

                await queryClient.invalidateQueries(trpc.agent.listSessionsEnriched.pathFilter());
            }
        }),
        { successMessage: 'Model updated', fallbackErrorMessage: 'Failed to update model' }
    );

    const setInferenceProfileMutation = useTrpcMutation(
        trpc.agent.setSessionInferenceProfile.mutationOptions({
            onSuccess: async () => {
                if (!sessionId) return;

                await queryClient.invalidateQueries({
                    queryKey: trpc.agent.getSession.queryOptions({ sessionId, messagesLimit }).queryKey
                });

                await queryClient.invalidateQueries(trpc.agent.listSessionsEnriched.pathFilter());
            }
        }),
        { successMessage: 'Reasoning updated', fallbackErrorMessage: 'Failed to update reasoning' }
    );

    const renameMutation = useTrpcMutation(
        trpc.agent.renameSession.mutationOptions({
            onSuccess: async () => {
                if (!sessionId) return;

                await queryClient.invalidateQueries({
                    queryKey: trpc.agent.getSession.queryOptions({ sessionId, messagesLimit }).queryKey
                });

                await queryClient.invalidateQueries(trpc.agent.listSessionsEnriched.pathFilter());
            }
        }),
        { successMessage: 'Chat renamed', fallbackErrorMessage: 'Failed to rename chat' }
    );

    const [searchParams, setSearchParams] = useSearchParams();
    const setSearchParamsRef = useRef(setSearchParams);

    useEffect(() => {
        setSearchParamsRef.current = setSearchParams;
    }, [setSearchParams]);

    const highlightMessageId = useMemo(() => {
        const raw = searchParams.get('highlight');
        if (!raw) return null;
        const parsed = Number(raw);
        return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
    }, [searchParams]);

    const [highlightedId, setHighlightedId] = useState<number | null>(null);
    const didScrollToHighlightRef = useRef<number | null>(null);

    const highlightIsToolMessage = useMemo(() => {
        if (!highlightMessageId) return false;
        const found = messages.find((m) => m.id === highlightMessageId);
        return Boolean(found && (found.role === 'tool_call' || found.role === 'tool_result'));
    }, [highlightMessageId, messages]);

    const effectiveShowTools = showTools || highlightIsToolMessage;

    useEffect(() => {
        if (!highlightMessageId) return;
        setHighlightedId(highlightMessageId);

        requestAnimationFrame(() => {
            const el = document.getElementById(`chat-message-${highlightMessageId}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });

        const timer = setTimeout(() => {
            setHighlightedId(null);

            setSearchParamsRef.current(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('highlight');
                    return next;
                },
                { replace: true }
            );
        }, 3000);

        return () => clearTimeout(timer);
    }, [highlightMessageId]);

    useEffect(() => {
        if (!highlightMessageId) {
            didScrollToHighlightRef.current = null;
            return;
        }

        if (didScrollToHighlightRef.current === highlightMessageId) return;

        const el = document.getElementById(`chat-message-${highlightMessageId}`);

        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            didScrollToHighlightRef.current = highlightMessageId;
        }
    }, [highlightMessageId, messages.length]);

    const inferenceProfiles = useMemo(() => inferenceProfilesQuery.data?.profiles ?? [], [inferenceProfilesQuery.data?.profiles]);
    const sessionInferenceProfileKey = sessionQuery.data?.session?.inferenceProfileKey ?? null;
    const effectiveInferenceProfileDisplayName = sessionQuery.data?.effectiveInferenceProfile?.displayName ?? null;
    const enabledModels = useMemo(() => modelsQuery.data?.models ?? [], [modelsQuery.data?.models]);
    const sessionModelKey = sessionQuery.data?.session?.modelKey ?? null;

    const serverMessages = useMemo<ChatMessage[]>(() => {
        const rows = sessionQuery.data?.messages ?? [];
        const out: ChatMessage[] = [];

        for (const m of rows) {
            if (!isDisplayableRole(m.role)) continue;

            if (m.role === 'user' || m.role === 'assistant') {
                const content = (m.content ?? '').trim();
                if (!content) continue;
                out.push({ id: m.id, role: m.role, content });
                continue;
            }

            if (m.role === 'tool_call') {
                if (!m.toolCallId || !m.toolName || !m.toolInputJson) continue;

                out.push({
                    id: m.id,
                    role: 'tool_call',
                    toolCallId: m.toolCallId,
                    toolName: m.toolName,
                    toolInputJson: m.toolInputJson,
                    requiresConfirmation: Boolean(m.requiresConfirmation),
                    confirmationStatus: m.confirmationStatus ?? null,
                    actionDescription: m.content ?? undefined
                });

                continue;
            }

            if (m.role === 'tool_result') {
                if (!m.toolCallId || !m.toolName || !m.toolResultJson) continue;

                out.push({
                    id: m.id,
                    role: 'tool_result',
                    toolCallId: m.toolCallId,
                    toolName: m.toolName,
                    toolResultJson: m.toolResultJson
                });
            }
        }

        return out;
    }, [sessionQuery.data?.messages]);

    useEffect(() => {
        if (sessionId === null) return;
        if (isStreaming) return;
        if (!sessionQuery.data) return;
        setMessages(serverMessages);
    }, [isStreaming, serverMessages, sessionId, sessionQuery.data]);

    useEffect(() => {
        if (!sessionQuery.error) return;
        handleTrpcQuery(sessionQuery, { fallbackErrorMessage: 'Failed to load chat session', showErrorToast: true });
    }, [sessionQuery.error]);

    useEffect(() => {
        if (!inferenceProfilesQuery.error) return;

        handleTrpcQuery(inferenceProfilesQuery, {
            fallbackErrorMessage: 'Failed to load reasoning options',
            showErrorToast: false
        });
    }, [inferenceProfilesQuery.error]);

    useEffect(() => {
        if (!modelsQuery.error) return;
        handleTrpcQuery(modelsQuery, { fallbackErrorMessage: 'Failed to load model options', showErrorToast: false });
    }, [modelsQuery.error]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ block: 'end' });
    }, [messages, streamingAssistantText, isStreaming]);

    const handleStreamEvent = useCallback(
        (event: AgentSseEvent) => {
            if (event.type === 'token') {
                streamingAssistantRef.current += event.token;
                if (typeof window === 'undefined') return;
                if (streamingUpdateRafRef.current !== null) return;

                streamingUpdateRafRef.current = requestAnimationFrame(() => {
                    streamingUpdateRafRef.current = null;
                    setStreamingAssistantText(streamingAssistantRef.current);
                });
            }

            if (event.type === 'message') {
                flushStreamingAssistant();
                const m = event.message;

                setMessages((prev) => {
                    if (typeof m.id === 'number' && prev.some((x) => x.id === m.id)) return prev;

                    if (m.role === 'user' || m.role === 'assistant') {
                        const content = (m.content ?? '').trim();
                        if (!content) return prev;
                        return [...prev, { id: m.id, role: m.role, content }];
                    }

                    if (m.role === 'tool_call') {
                        if (!m.toolCallId || !m.toolName || !m.toolInputJson) return prev;
                        return [
                            ...prev,
                            {
                                id: m.id,
                                role: 'tool_call',
                                toolCallId: m.toolCallId,
                                toolName: m.toolName,
                                toolInputJson: m.toolInputJson,
                                requiresConfirmation: Boolean(m.requiresConfirmation),
                                confirmationStatus: m.confirmationStatus,
                                actionDescription: m.content ?? undefined
                            }
                        ];
                    }

                    if (m.role === 'tool_result') {
                        if (!m.toolCallId || !m.toolName || !m.toolResultJson) return prev;
                        return [
                            ...prev,
                            {
                                id: m.id,
                                role: 'tool_result',
                                toolCallId: m.toolCallId,
                                toolName: m.toolName,
                                toolResultJson: m.toolResultJson
                            }
                        ];
                    }

                    return prev;
                });

                if (
                    isAutoApproveEnabled &&
                    m.role === 'tool_call' &&
                    m.toolCallId &&
                    m.requiresConfirmation &&
                    m.confirmationStatus === 'pending'
                ) {
                    setPendingAutoApproveToolCallIds((prev) => (prev.includes(m.toolCallId!) ? prev : [...prev, m.toolCallId!]));
                }
            }

            if (event.type === 'tool_call') {
                flushStreamingAssistant();

                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'tool_call',
                        toolCallId: event.toolCallId,
                        toolName: event.toolName,
                        toolInputJson: event.toolInputJson,
                        requiresConfirmation: false,
                        confirmationStatus: null
                    }
                ]);
            }

            if (event.type === 'tool_result') {
                flushStreamingAssistant();

                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'tool_result',
                        toolCallId: event.toolCallId,
                        toolName: event.toolName,
                        toolResultJson: event.toolResultJson
                    }
                ]);
            }

            if (event.type === 'confirmation_request') {
                flushStreamingAssistant();

                setMessages((prev) => {
                    if (prev.some((m) => m.role === 'tool_call' && m.toolCallId === event.toolCallId)) return prev;
                    return [
                        ...prev,
                        {
                            role: 'tool_call',
                            toolCallId: event.toolCallId,
                            toolName: event.toolName,
                            toolInputJson: event.toolInputJson,
                            requiresConfirmation: true,
                            confirmationStatus: 'pending',
                            actionDescription: event.actionDescription
                        }
                    ];
                });

                if (isAutoApproveEnabled) {
                    setPendingAutoApproveToolCallIds((prev) => (prev.includes(event.toolCallId) ? prev : [...prev, event.toolCallId]));
                }
            }

            if (event.type === 'status') {
                if (typeof event.runId === 'number' && event.runId > 0) {
                    setCurrentRunId(event.runId);
                }
            }

            if (event.type === 'error') showToast(event.message, 'error');
        },
        [flushStreamingAssistant, isAutoApproveEnabled]
    );

    const finalizeStream = useCallback(
        (done: Extract<AgentSseEvent, { type: 'done' }> | null) => {
            const resolvedSessionId = done?.sessionId && done.sessionId > 0 ? done.sessionId : sessionId;

            if (resolvedSessionId && !sessionId) {
                createdSessionIdRef.current = resolvedSessionId;
                setSessionId(resolvedSessionId);
            }

            const assistantText = streamingAssistantRef.current.trim();

            if (assistantText) {
                setMessages((prev) => [...prev, { role: 'assistant', content: assistantText }]);
                setStreamingAssistantText('');
                streamingAssistantRef.current = '';
            }

            setCurrentRunId(null);

            if (resolvedSessionId) {
                void queryClient.invalidateQueries(trpc.agent.listSessionsEnriched.pathFilter());

                void queryClient.invalidateQueries({
                    queryKey: trpc.agent.getSession.queryOptions({ sessionId: resolvedSessionId, messagesLimit }).queryKey
                });
            }
        },
        [messagesLimit, queryClient, sessionId, setSessionId]
    );

    const sendMessage = useCallback(
        async (message: string) => {
            const trimmed = message.trim();
            if (!trimmed) return;

            setStreamingAssistantText('');
            streamingAssistantRef.current = '';
            setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);

            try {
                const done = await streamMessage({
                    sessionId,
                    message: trimmed,
                    pageContextJson: JSON.stringify(pageContextRef.current),
                    executionMode,
                    onEvent: handleStreamEvent
                });

                finalizeStream(done);
            } catch (e) {
                const message = e instanceof Error ? e.message : 'Failed to send message';
                showToast(message, 'error');
            }
        },
        [executionMode, finalizeStream, handleStreamEvent, sessionId, streamMessage]
    );

    const decideConfirmation = useCallback(
        async (params: { toolCallId: string; approved: boolean }) => {
            setMessages((prev) =>
                prev.map((m) => {
                    if (m.role !== 'tool_call') return m;
                    if (m.toolCallId !== params.toolCallId) return m;
                    return { ...m, confirmationStatus: params.approved ? ('approved' as const) : ('rejected' as const) };
                })
            );

            setStreamingAssistantText('');
            streamingAssistantRef.current = '';

            try {
                const done = await confirmToolCall({
                    toolCallId: params.toolCallId,
                    approved: params.approved,
                    executionMode,
                    onEvent: handleStreamEvent
                });

                finalizeStream(done);
            } catch (e) {
                const message = e instanceof Error ? e.message : 'Failed to confirm tool call';
                showToast(message, 'error');

                // Revert confirmation status so user can retry
                setMessages((prev) =>
                    prev.map((m) => {
                        if (m.role !== 'tool_call') return m;
                        if (m.toolCallId !== params.toolCallId) return m;
                        return { ...m, confirmationStatus: 'pending' as const };
                    })
                );
            }
        },
        [confirmToolCall, executionMode, finalizeStream, handleStreamEvent]
    );

    useEffect(() => {
        if (!isAutoApproveEnabled) return;
        if (pendingAutoApproveToolCallIds.length === 0) return;
        if (isStreaming) return;

        const nextToolCallId = pendingAutoApproveToolCallIds[0];
        if (!nextToolCallId) return;

        setPendingAutoApproveToolCallIds((prev) => prev.filter((id) => id !== nextToolCallId));
        void decideConfirmation({ toolCallId: nextToolCallId, approved: true });
    }, [decideConfirmation, isAutoApproveEnabled, isStreaming, pendingAutoApproveToolCallIds]);

    const handleStop = useCallback(() => {
        const runIdToCancel = currentRunId ?? sessionQuery.data?.activeRun?.runId ?? null;

        if (runIdToCancel) {
            cancelRun({ runId: runIdToCancel }).catch((e) => {
                const message = e instanceof Error ? e.message : 'Failed to cancel run';
                showToast(message, 'error');
                abort();
            });
        } else {
            abort();
        }
    }, [abort, cancelRun, currentRunId, sessionQuery.data?.activeRun?.runId]);

    const initialContextJson = sessionQuery.data?.session?.initialContextJson ?? null;
    const agentMode = useMemo(() => extractAgentMode(initialContextJson), [initialContextJson]);
    const autopilotPlanIdFromContext = useMemo(() => extractAutopilotPlanId({ initialContextJson }), [initialContextJson]);

    const shouldShowAutopilotActionBar =
        agentMode === 'strategy_autopilot_plan_builder' && autopilotPlanIdFromContext !== null && autopilotPlanIdFromContext > 0;

    const sessionTitle = sessionQuery.data?.session?.title ?? null;
    const activeRun = sessionQuery.data?.activeRun ?? null;
    const activeRunId = activeRun?.runId ?? null;
    const activeRunStatus = activeRun?.status ?? null;
    const activeRunIsLive = Boolean(activeRun?.isLive);
    const isBusy = isStreaming || activeRunIsLive || activeRunStatus === 'waiting_confirmation';
    const effectiveStatus = status ?? (activeRunStatus === 'running' && !activeRunIsLive ? 'stalled' : activeRunStatus);

    const handleRenameTitle = useCallback(
        (title: string) => {
            if (!sessionId) return;
            renameMutation.mutate({ sessionId, title });
        },
        [renameMutation, sessionId]
    );

    return (
        <div className="flex flex-col h-full">
            {sessionId !== null && (
                <div className="flex items-center gap-2 px-3 py-1.5 border-b border-zinc-800 shrink-0">
                    {onToggleSidebar && (
                        <button
                            className="text-xs px-2 py-1 rounded-md bg-zinc-800 hover:bg-zinc-700 text-white"
                            title={isSidebarVisible ? 'Hide chat list' : 'Show chat list'}
                            type="button"
                            onClick={onToggleSidebar}
                        >
                            {isSidebarVisible ? 'Hide Chats' : 'Show Chats'}
                        </button>
                    )}
                    <InlineEditableTitle
                        className="text-sm font-medium text-white"
                        value={(sessionTitle ?? '').trim() || `Chat #${sessionId}`}
                        onSave={handleRenameTitle}
                    />
                </div>
            )}

            <ChatMessagesPanel
                decideConfirmation={decideConfirmation}
                effectiveShowTools={effectiveShowTools}
                endRef={endRef}
                highlightedId={highlightedId}
                isBusy={isBusy}
                isStreaming={isStreaming}
                messages={messages}
                status={effectiveStatus}
                streamingAssistantText={streamingAssistantText}
            />

            <div className="border-t border-zinc-800 px-3 py-2 space-y-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <label className="flex items-center gap-1">
                        <span className="text-[11px] text-zinc-500">Mode</span>
                        <select
                            className="rounded bg-zinc-800/60 px-1.5 py-0.5 text-[11px] text-white border-none focus:outline-none focus:ring-1 focus:ring-blue-500/40"
                            disabled={isStreaming}
                            value={executionMode}
                            onChange={(e) => setExecutionMode(e.target.value === 'queued' ? 'queued' : 'inline')}
                        >
                            <option value="inline">Live</option>
                            <option value="queued">Background</option>
                        </select>
                    </label>

                    <label className="flex items-center gap-1">
                        <span className="text-[11px] text-zinc-500">Model</span>
                        <select
                            className="rounded bg-zinc-800/60 px-1.5 py-0.5 text-[11px] text-white border-none focus:outline-none focus:ring-1 focus:ring-blue-500/40"
                            disabled={
                                isStreaming ||
                                !sessionId ||
                                !sessionModelKey ||
                                setSessionModelMutation.isPending ||
                                enabledModels.length === 0 ||
                                modelsQuery.isPending
                            }
                            value={sessionModelKey ?? ''}
                            onChange={(e) => {
                                if (!sessionId) {
                                    showToast('Start a chat first to change model', 'info');
                                    return;
                                }

                                const nextModelKey = e.target.value.trim();
                                if (!nextModelKey) return;
                                void setSessionModelMutation.mutateAsync({ sessionId, modelKey: nextModelKey });
                            }}
                        >
                            {(!sessionModelKey || enabledModels.length === 0) && <option value="">Loading…</option>}
                            {enabledModels
                                .slice()
                                .sort((a, b) => {
                                    const p = a.provider.localeCompare(b.provider);
                                    if (p !== 0) return p;
                                    return a.displayName.localeCompare(b.displayName);
                                })
                                .map((m) => (
                                    <option key={m.modelKey} value={m.modelKey}>
                                        {formatModelLabel({ displayName: m.displayName, provider: m.provider, isDefault: m.isDefault })}
                                    </option>
                                ))}
                        </select>
                    </label>

                    <label className="flex items-center gap-1">
                        <span className="text-[11px] text-zinc-500">Reasoning</span>
                        <select
                            className="rounded bg-zinc-800/60 px-1.5 py-0.5 text-[11px] text-white border-none focus:outline-none focus:ring-1 focus:ring-blue-500/40"
                            disabled={isStreaming || !sessionId || setInferenceProfileMutation.isPending}
                            value={sessionInferenceProfileKey ?? ''}
                            onChange={(e) => {
                                if (!sessionId) {
                                    showToast('Start a chat first to change reasoning', 'info');
                                    return;
                                }

                                const raw = e.target.value;
                                const nextKey = raw === '' ? null : raw;
                                void setInferenceProfileMutation.mutateAsync({ sessionId, inferenceProfileKey: nextKey });
                            }}
                        >
                            <option value="">{formatInferenceDefaultLabel(effectiveInferenceProfileDisplayName)}</option>
                            {inferenceProfiles.map((p) => (
                                <option key={p.key} value={p.key}>
                                    {p.displayName}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="flex items-center gap-1">
                        <span className="text-[11px] text-zinc-500">Tools</span>
                        <select
                            className="rounded bg-zinc-800/60 px-1.5 py-0.5 text-[11px] text-white border-none focus:outline-none focus:ring-1 focus:ring-blue-500/40"
                            title="Hide or show tool calls/results (confirmation prompts always show)"
                            value={showTools ? 'show' : 'hide'}
                            onChange={(e) => setShowTools(e.target.value === 'show')}
                        >
                            <option value="hide">Hide</option>
                            <option value="show">Show</option>
                        </select>
                    </label>

                    <label className="flex items-center gap-1">
                        <span className={`text-[11px] ${isAutoApproveEnabled ? 'text-amber-400' : 'text-zinc-500'}`}>Approvals</span>
                        <select
                            className={`rounded px-1.5 py-0.5 text-[11px] border-none focus:outline-none focus:ring-1 focus:ring-blue-500/40 ${
                                isAutoApproveEnabled
                                    ? 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30'
                                    : 'bg-zinc-800/60 text-white'
                            }`}
                            disabled={isStreaming}
                            value={isAutoApproveEnabled ? 'auto' : 'manual'}
                            onChange={(e) => {
                                const nextValue = e.target.value === 'auto';

                                if (nextValue) {
                                    if (isAutoApproveEnabled) return;
                                    setIsAutoApproveWarningOpen(true);
                                    return;
                                }

                                setIsAutoApproveEnabled(false);
                                setPendingAutoApproveToolCallIds([]);
                            }}
                        >
                            <option value="manual">Manual</option>
                            <option value="auto">Auto-approve</option>
                        </select>
                    </label>
                </div>

                {(effectiveStatus || streamError) && (
                    <div className="text-xs text-zinc-500">
                        {effectiveStatus ? `Status: ${effectiveStatus}` : null}
                        {effectiveStatus && streamError ? ' • ' : null}
                        {streamError ? `Error: ${streamError}` : null}
                    </div>
                )}

                {shouldShowAutopilotActionBar && autopilotPlanIdFromContext !== null && (
                    <AutopilotActionBar
                        isBusy={isBusy}
                        planId={autopilotPlanIdFromContext}
                        onNavigateToSession={(id) => setSessionId(id)}
                    />
                )}

                <ChatInput
                    autoFocus={autoFocus}
                    currentRunId={currentRunId ?? activeRunId}
                    isBusy={isBusy}
                    key={sessionId ?? 'new'}
                    onSend={sendMessage}
                    onStop={handleStop}
                />
                <div className="text-[11px] text-zinc-500">Enter to send • Shift+Enter for newline</div>
            </div>

            <ConfirmationDialog
                cancelText="Cancel"
                confirmText="Enable auto-approve"
                confirmVariant="warning"
                isOpen={isAutoApproveWarningOpen}
                message={
                    <div className="space-y-3">
                        <div>
                            Auto-approve will automatically approve <span className="font-semibold text-white">all</span> agent confirmation
                            prompts.
                        </div>
                        <div className="text-sm">
                            This can create/edit/delete data, start jobs/experiments, and potentially make expensive or destructive changes.
                            Use at your own risk.
                        </div>
                    </div>
                }
                title="Enable auto-approve?"
                onCancel={() => setIsAutoApproveWarningOpen(false)}
                onConfirm={() => {
                    setIsAutoApproveEnabled(true);
                    setIsAutoApproveWarningOpen(false);
                }}
            />
        </div>
    );
}
